/**
 * TrackedParlaysSummaryWidget Component
 * Compact summary widget that replaces the full TrackedParlaysPanel sidebar.
 * Shows key stats at a glance with a link to the dedicated My Bets view.
 */

import type { ParlayStats } from '@hooks/useTrackedParlays';
import { hitRatePct } from '@parlay-picker/shared/parlayStats';
import { cn } from '@utils/cn';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import type { ReactElement } from 'react';

// =============================================================================
// Types
// =============================================================================

interface TrackedParlaysSummaryWidgetProps {
  stats: ParlayStats;
  onNavigateToParlays: () => void;
}

// =============================================================================
// Component
// =============================================================================

export function TrackedParlaysSummaryWidget({
  stats,
  onNavigateToParlays,
}: TrackedParlaysSummaryWidgetProps): ReactElement | null {
  // Don't show widget if no tracked parlays at all
  if (stats.total === 0) return null;

  // Decided = won + lost only; push/void/pending stay out of the denominator.
  const wonPct = hitRatePct(stats.won, stats.lost);
  const lostPct = hitRatePct(stats.lost, stats.won);
  const hitRate = wonPct != null ? wonPct.toFixed(1) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className="rounded-xl border p-4 cursor-pointer transition-all group bg-theme-surface border-theme-border hover:border-theme-primary/40 hover:shadow-lg hover:shadow-theme-primary/5"
      onClick={onNavigateToParlays}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-theme-text">My Bets</h3>
        <span className="text-xs font-medium flex items-center gap-1 transition-colors text-theme-primary group-hover:text-theme-primary">
          View All
          <ChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2">
        {/* Total */}
        <div className="text-center">
          <div className="text-xl font-bold text-theme-text">{stats.total}</div>
          <div className="text-[10px] uppercase tracking-wider text-theme-text-tertiary">Total</div>
        </div>

        {/* Win Rate */}
        <div className="text-center">
          <div
            className={cn(
              'text-xl font-bold',
              hitRate
                ? Number(hitRate) >= 30
                  ? 'text-theme-success'
                  : Number(hitRate) >= 20
                    ? 'text-theme-text'
                    : 'text-theme-danger'
                : 'text-theme-text-secondary',
            )}
          >
            {hitRate ? `${hitRate}%` : '-'}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-theme-text-tertiary">
            Hit Rate
          </div>
        </div>

        {/* Pending */}
        <div className="text-center">
          <div
            className={cn(
              'text-xl font-bold',
              stats.pending > 0 ? 'text-theme-warning' : 'text-theme-text-secondary',
            )}
          >
            {stats.pending}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-theme-text-tertiary">
            Pending
          </div>
        </div>
      </div>

      {/* W-L record bar */}
      {wonPct != null && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-[10px] mb-1">
            <span className="text-theme-success">{stats.won}W</span>
            <span className="text-theme-danger">{stats.lost}L</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden flex bg-theme-border/30">
            <div
              className="h-full rounded-l-full transition-all bg-theme-success"
              style={{ width: `${wonPct}%` }}
            />
            <div
              className="h-full rounded-r-full transition-all bg-theme-danger"
              style={{ width: `${lostPct ?? 0}%` }}
            />
          </div>
        </div>
      )}
    </motion.div>
  );
}
